import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import partnershipsApi from "./api/partnershipsApi";
import {
  CreatePartnerInvitation,
  CustomerPartnershipsState,
  InvitationQueryParameter,
  PartnerInvitationsQuery,
  PartnershipQueryParameter,
  PartnershipsQuery,
} from "./models/models";

// ─── Thunks ──────────────────────────────────────────────────

export const getSentInvitations = createAsyncThunk(
  "customerPartnerships/getSentInvitations",
  async (query: PartnerInvitationsQuery) => {
    return partnershipsApi.getSentInvitations(query);
  }
);

export const getReceivedInvitations = createAsyncThunk(
  "customerPartnerships/getReceivedInvitations",
  async (query: PartnerInvitationsQuery) => {
    return partnershipsApi.getReceivedInvitations(query);
  }
);

export const getInvitation = createAsyncThunk(
  "customerPartnerships/getInvitation",
  async (id: string) => {
    return partnershipsApi.getInvitation(id);
  }
);

export const sendInvitation = createAsyncThunk(
  "customerPartnerships/sendInvitation",
  async (data: CreatePartnerInvitation) => {
    return partnershipsApi.sendInvitation(data);
  }
);

export const acceptInvitation = createAsyncThunk(
  "customerPartnerships/acceptInvitation",
  async (id: string) => {
    await partnershipsApi.acceptInvitation(id);
    return id;
  }
);

export const declineInvitation = createAsyncThunk(
  "customerPartnerships/declineInvitation",
  async (id: string) => {
    await partnershipsApi.declineInvitation(id);
    return id;
  }
);

export const cancelInvitation = createAsyncThunk(
  "customerPartnerships/cancelInvitation",
  async (id: string) => {
    await partnershipsApi.cancelInvitation(id);
    return id;
  }
);

export const resendInvitation = createAsyncThunk(
  "customerPartnerships/resendInvitation",
  async (id: string) => {
    await partnershipsApi.resendInvitation(id);
    return id;
  }
);

export const getPartnerships = createAsyncThunk(
  "customerPartnerships/getPartnerships",
  async (query: PartnershipsQuery) => {
    return partnershipsApi.getPartnerships(query);
  }
);

export const getPartnership = createAsyncThunk(
  "customerPartnerships/getPartnership",
  async (id: string) => {
    return partnershipsApi.getPartnership(id);
  }
);

export const getPartnershipStats = createAsyncThunk(
  "customerPartnerships/getPartnershipStats",
  async () => {
    return partnershipsApi.getStats();
  }
);

export const getPartnershipWith = createAsyncThunk(
  "customerPartnerships/getPartnershipWith",
  async (customerId: string, { rejectWithValue }) => {
    try {
      return await partnershipsApi.getPartnershipWith(customerId);
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      return rejectWithValue(err);
    }
  }
);

export const deactivatePartnership = createAsyncThunk(
  "customerPartnerships/deactivatePartnership",
  async (id: string) => {
    return partnershipsApi.deactivatePartnership(id);
  }
);

// ─── Initial State ───────────────────────────────────────────

const initialState: CustomerPartnershipsState = {
  invitationsSent: undefined,
  invitationsReceived: undefined,
  partnerships: undefined,
  partnershipWith: undefined,
  stats: undefined,
  selectedInvitation: undefined,
  sentQuery: {
    pageNo: 1,
    pageSize: 20,
    sortBy: InvitationQueryParameter.createdDate,
    sortDesc: true,
  },
  receivedQuery: {
    pageNo: 1,
    pageSize: 20,
    sortBy: InvitationQueryParameter.createdDate,
    sortDesc: true,
  },
  partnershipsQuery: {
    pageNo: 1,
    pageSize: 20,
    sortBy: PartnershipQueryParameter.createdDate,
    sortDesc: true,
  },
  loading: false,
  actionLoading: false,
  error: undefined,
};

// ─── Slice ───────────────────────────────────────────────────

const customerPartnershipsSlice = createSlice({
  name: "customerPartnerships",
  initialState,
  reducers: {
    changeSentQuery: (state, action) => {
      state.sentQuery = action.payload;
    },
    changeReceivedQuery: (state, action) => {
      state.receivedQuery = action.payload;
    },
    changePartnershipsQuery: (state, action) => {
      state.partnershipsQuery = action.payload;
    },
    setError: (state, action) => {
      state.error = action.payload;
    },
    clearMessages: (state) => {
      state.error = undefined;
    },
  },
  extraReducers: (builder) => {
    // ─── Sent Invitations ─────────────────────────────────────
    builder
      .addCase(getSentInvitations.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSentInvitations.fulfilled, (state, { payload }) => {
        state.invitationsSent = payload;
        state.loading = false;
      })
      .addCase(getSentInvitations.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    // ─── Received Invitations ─────────────────────────────────
    builder
      .addCase(getReceivedInvitations.pending, (state) => {
        state.loading = true;
      })
      .addCase(getReceivedInvitations.fulfilled, (state, { payload }) => {
        state.invitationsReceived = payload;
        state.loading = false;
      })
      .addCase(getReceivedInvitations.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    // ─── Single Invitation ────────────────────────────────────
    builder
      .addCase(getInvitation.fulfilled, (state, { payload }) => {
        state.selectedInvitation = payload;
      });

    // ─── Send Invitation ──────────────────────────────────────
    builder
      .addCase(sendInvitation.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(sendInvitation.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(sendInvitation.rejected, (state, { error }) => {
        state.error = error;
        state.actionLoading = false;
      });

    // ─── Accept Invitation ────────────────────────────────────
    builder
      .addCase(acceptInvitation.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(acceptInvitation.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(acceptInvitation.rejected, (state, { error }) => {
        state.error = error;
        state.actionLoading = false;
      });

    // ─── Decline Invitation ───────────────────────────────────
    builder
      .addCase(declineInvitation.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(declineInvitation.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(declineInvitation.rejected, (state, { error }) => {
        state.error = error;
        state.actionLoading = false;
      });

    // ─── Cancel Invitation ────────────────────────────────────
    builder
      .addCase(cancelInvitation.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(cancelInvitation.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(cancelInvitation.rejected, (state, { error }) => {
        state.error = error;
        state.actionLoading = false;
      });

    // ─── Resend Invitation ────────────────────────────────────
    builder
      .addCase(resendInvitation.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(resendInvitation.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(resendInvitation.rejected, (state, { error }) => {
        state.error = error;
        state.actionLoading = false;
      });

    // ─── Partnerships ─────────────────────────────────────────
    builder
      .addCase(getPartnerships.pending, (state) => {
        state.loading = true;
      })
      .addCase(getPartnerships.fulfilled, (state, { payload }) => {
        state.partnerships = payload;
        state.loading = false;
      })
      .addCase(getPartnerships.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    // ─── Stats ────────────────────────────────────────────────
    builder
      .addCase(getPartnershipStats.fulfilled, (state, { payload }) => {
        state.stats = payload;
      });

    // ─── Partnership With ────────────────────────────────────
    builder
      .addCase(getPartnershipWith.fulfilled, (state, { payload }) => {
        state.partnershipWith = payload;
      })
      .addCase(getPartnershipWith.rejected, (state) => {
        state.partnershipWith = null;
      });

    // ─── Deactivate Partnership ──────────────────────────────
    builder
      .addCase(deactivatePartnership.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(deactivatePartnership.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(deactivatePartnership.rejected, (state, { error }) => {
        state.error = error;
        state.actionLoading = false;
      });
  },
});

export const {
  changeSentQuery,
  changeReceivedQuery,
  changePartnershipsQuery,
  setError,
  clearMessages,
} = customerPartnershipsSlice.actions;

export default customerPartnershipsSlice.reducer;
